import InfiniteScroll from '@/presentation/components/modules/InfiniteScroll';
import { useFormatter, useTranslations } from 'use-intl';
import PopularItems from '../../modules/PopularItems';
import { Children, ReactNode } from 'react';
import { QueryStatus } from '@tanstack/react-query';
import { SwapType } from '@/domain/swap';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import { formatEther } from 'viem';

export interface TokensProps {
  popularTokens: ReactNode;
  tokens: ReactNode;
  hasMoreTokens: boolean;
  onMoreClick: () => void;
  status: QueryStatus;
  type: SwapType;
  onChooseEth: () => void;
  ethBalance?: bigint;
}

export const Tokens = ({
  popularTokens,
  hasMoreTokens,
  onMoreClick,
  type,
  onChooseEth,
  ethBalance,
  tokens,
  status,
}: TokensProps) => {
  const t = useTranslations('Swap');
  const { number } = useFormatter();
  return (
    <>
      <header>
        <div className="px-4 pt-2">
          <h3 className="text-gray-400 font-semibold text-xs uppercase my-2">
            {t('Search.popular')}
          </h3>
          <PopularItems items={popularTokens} />
        </div>
      </header>
      <section className="">
        <h3 className="text-gray-400 font-semibold text-xs uppercase mx-4 my-2">
          {t('Search.all')}
        </h3>
        <ul>
          <InfiniteScroll
            key="tokens-infinite-scroll"
            loaderElement="li"
            scrollable={hasMoreTokens}
            onLoadMore={onMoreClick}
          >
            <li key={'ether'}>
              <button
                type="button"
                className={`w-full flex items-center hover:bg-gray-200 px-4 py-2 text-gray-800 ${
                  type === 'ETH' ? 'opacity-50 cursor-default' : ''
                }`}
                onClick={onChooseEth}
              >
                <div className="w-10 h-10 mr-2 flex items-center justify-center bg-blue-500 rounded-full">
                  <Icon name={Icons.ETH} className="text-gray-50" />
                </div>
                <div className="flex flex-col items-start">
                  <h4 className="font-medium">{'ETH'}</h4>
                  <div className="text-sm text-gray-500">{'Ether'}</div>
                </div>
                <div className="ml-auto">
                  {number(Number(formatEther(ethBalance || 0n)), {
                    maximumSignificantDigits: 4,
                    minimumSignificantDigits: 2,
                  })}
                </div>
                {type === 'ETH' && (
                  <Icon
                    name={Icons.CHECK}
                    size={20}
                    className="ml-2 text-pink-500"
                  />
                )}
              </button>
            </li>
            {status === 'loading' ? (
              new Array(5).fill(null).map((x, i) => (
                <div
                  className="w-full flex items-center px-4 py-2 animate animate-pulse"
                  key={i}
                >
                  <div className="w-8 h-8 flex-none mr-2 rounded-full bg-gray-300/50" />
                  <div className="h-6 w-1/4 bg-gray-300/50 rounded-sm" />
                  <div className="ml-auto h-4 w-1/3 bg-gray-300/50 rounded-sm" />
                </div>
              ))
            ) : Children.count(tokens) ? (
              tokens
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                {'Nothing to see here :('}
              </div>
            )}
          </InfiniteScroll>
        </ul>
      </section>
    </>
  );
};

export default Tokens;
